import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { PhoneInput } from "@freightsimple/ui-core/Controls/PhoneInput";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import type { ContactErrors } from "../Validators/contactsErrorMessagesForContact";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";

interface ContactInputProps {
  contactChanged: (_: Contact) => void;
  contact: Contact;
  errorMessages: ContactErrors | undefined;
  warningMessages: ContactErrors | undefined;
  forceValidation: boolean;
  customLabels?: {
    contactName?: string | undefined;
    phoneNumber?: string | undefined;
    emailAddress?: string | undefined;
  };
  /** Character caps for the text fields — see the same prop on
   *  LocationsAddressInput. */
  maxLengths?: { contactName: number; emailAddress: number } | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
const ContactsInput: React.FC<ContactInputProps> = (
  props: ContactInputProps,
) => {
  // A field shows its warning only when it has no error — the error wins.
  const contactNameWarning =
    props.errorMessages?.contactName === undefined
      ? props.warningMessages?.contactName
      : undefined;
  const emailWarning =
    props.errorMessages?.emailAddress === undefined
      ? props.warningMessages?.emailAddress
      : undefined;

  return (
    <ShowFormErrors show={props.forceValidation}>
      <Stack gap={4} align="stretch">
        <FormItem
          label={props.customLabels?.contactName || "Contact Name"}
          error={props.errorMessages?.contactName}
          warning={contactNameWarning}
          fillWidth
        >
          <TextInput
            name="contact_contact_name"
            maxLength={props.maxLengths?.contactName}
            value={props.contact?.contactName || ""}
            onChange={(e) =>
              props.contactChanged({
                ...props.contact,
                contactName: e.target.value,
              })
            }
            width="full"
          />
        </FormItem>
        {/* Phone (with extension) needs a little more room than email — the
            5:4 ratio mirrors the legacy 270:250 split. */}
        <Grid columns={[5, 4]} gap="sm" minColumnWidth={220}>
          <FormItem
            label={props.customLabels?.phoneNumber || "Contact Phone Number"}
            error={props.errorMessages?.phoneNumber}
            fillWidth
          >
            <PhoneInput
              phoneNumber={props.contact.phoneNumber}
              extension={props.contact.phoneNumberExtension}
              onPhoneNumberChange={(value: string) => {
                props.contactChanged({
                  ...props.contact,
                  phoneNumber: value,
                });
              }}
              onExtensionChange={(value: string) => {
                props.contactChanged({
                  ...props.contact,
                  phoneNumberExtension: value,
                });
              }}
              width="full"
            />
          </FormItem>
          <FormItem
            label={
              props.customLabels?.emailAddress || "Email Address (optional)"
            }
            error={props.errorMessages?.emailAddress}
            warning={emailWarning}
            fillWidth
          >
            <TextInput
              type="email"
              name="contact_email_address"
              maxLength={props.maxLengths?.emailAddress}
              value={props.contact?.emailAddress || ""}
              onChange={(e) =>
                props.contactChanged({
                  ...props.contact,
                  emailAddress: e.target.value,
                })
              }
              width="full"
            />
          </FormItem>
        </Grid>
      </Stack>
    </ShowFormErrors>
  );
};
export default ContactsInput;
